// src/components/OnboardingFlow.jsx
// Post-signup onboarding wizard — shown once per new user
// Tracked via localStorage key 'dd_onboarding_done'

import React, { useState } from 'react';

const mono = 'JetBrains Mono, monospace';
const display = 'Syne, sans-serif';

const TOTAL_STEPS = 4;

// ── Role data ────────────────────────────────────────────────────────────────
const ROLES = [
  {
    id: 'learner',
    icon: '🎓',
    label: 'Learner',
    sub: 'I want to learn ML, DL, NLP, and AI concepts interactively',
    color: 'var(--cyan)',
    recommendation: {
      module: 'labs',
      icon: '🧭',
      label: 'Interactive Labs',
      desc: 'Start with Machine Learning labs. Walk through regression, classification, clustering, and neural networks — all hands-on, in the browser.',
      color: 'var(--cyan)',
    },
  },
  {
    id: 'builder',
    icon: '🛠️',
    label: 'Builder',
    sub: 'I want to run experiments, analyze data, and build AI pipelines',
    color: 'var(--green)',
    recommendation: {
      module: 'auto_eda',
      icon: '📊',
      label: 'Auto EDA',
      desc: 'Upload a CSV and get an instant data profile. Clean, visualize, find correlations, and export a report in minutes — no code needed.',
      color: 'var(--green)',
    },
  },
  {
    id: 'researcher',
    icon: '🔬',
    label: 'Researcher',
    sub: 'I want AI agents, document analysis, and multi-modal research workflows',
    color: 'var(--violet)',
    recommendation: {
      module: 'doc_analysis',
      icon: '📄',
      label: 'Document Analysis',
      desc: 'Upload a research paper or document. Extract insights, summaries, entities, sentiment, and keywords automatically.',
      color: 'var(--violet)',
    },
  },
];

// ── Free tier quotas ─────────────────────────────────────────────────────────
const FREE_QUOTAS = [
  { label: 'Auto EDA',           cap: 10 },
  { label: 'Document Analysis',  cap: 10 },
  { label: 'Image Insights',     cap: 100 },
  { label: 'Speech Studio',      cap: 10 },
  { label: 'ML Experiments',     cap: 10 },
  { label: 'DL Experiments',     cap: 10 },
  { label: 'Video Intelligence', cap: 10 },
];

// ── Step indicator ────────────────────────────────────────────────────────────
function StepDots({ current }) {
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 28 }}>
      {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
        <div
          key={i}
          style={{
            width: i === current ? 20 : 6,
            height: 6,
            borderRadius: 3,
            background: i === current ? 'var(--cyan)' : i < current ? 'var(--b2)' : 'var(--b1)',
            transition: 'all .3s ease',
          }}
        />
      ))}
    </div>
  );
}

// ── Progress bar ─────────────────────────────────────────────────────────────
function SmallBar({ pct, color = 'var(--cyan)' }) {
  return (
    <div style={{ height: 4, background: 'var(--b0)', borderRadius: 2, overflow: 'hidden', flex: 1 }}>
      <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 2, transition: 'width .4s ease' }} />
    </div>
  );
}

// ── Step 0: Welcome ───────────────────────────────────────────────────────────
function StepWelcome({ name, onNext }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 20 }}>👋</div>
      <div style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: 'var(--cyan)', letterSpacing: '.18em', marginBottom: 12 }}>
        WELCOME TO DEGEN DEN
      </div>
      <h2 style={{ fontFamily: display, fontWeight: 900, fontSize: 26, color: 'var(--t1)', marginBottom: 14, lineHeight: 1.2 }}>
        {name ? `Good to have you, ${name.split(' ')[0]}.` : 'You\'re in.'}
      </h2>
      <p style={{ fontSize: 12, color: 'var(--t2)', lineHeight: 1.9, maxWidth: 380, margin: '0 auto 32px' }}>
        Degen Den is a local-first AI research workspace — 50+ interactive labs, 7 end-to-end research workflows, built-in agents, and everything runs in your browser. Let's take 30 seconds to set your workspace up.
      </p>
      <button
        onClick={onNext}
        style={{
          padding: '12px 28px', background: 'var(--cyan)', border: 'none', borderRadius: 6,
          color: '#020508', fontFamily: mono, fontSize: 10, letterSpacing: '.12em', fontWeight: 900, cursor: 'pointer',
        }}
      >
        LET'S GO →
      </button>
    </div>
  );
}

// ── Step 1: Role picker ───────────────────────────────────────────────────────
function StepRole({ selected, onSelect, onNext, onBack }) {
  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div style={{ fontFamily: mono, fontSize: 9, color: 'var(--t3)', letterSpacing: '.14em', marginBottom: 10 }}>STEP 1 OF 3</div>
        <h2 style={{ fontFamily: display, fontWeight: 900, fontSize: 22, color: 'var(--t1)', lineHeight: 1.2 }}>
          What brings you here?
        </h2>
        <p style={{ fontSize: 12, color: 'var(--t2)', marginTop: 8 }}>We'll recommend the best starting point.</p>
      </div>
      <div style={{ display: 'grid', gap: 10, marginBottom: 28 }}>
        {ROLES.map((role) => (
          <button
            key={role.id}
            onClick={() => onSelect(role.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '14px 16px',
              background: selected === role.id ? `color-mix(in srgb, ${role.color} 10%, var(--surface))` : 'var(--surface)',
              border: `1px solid ${selected === role.id ? role.color : 'var(--b1)'}`,
              borderRadius: 8, cursor: 'pointer', textAlign: 'left', width: '100%', transition: 'all .15s',
            }}
          >
            <span style={{ fontSize: 24, flexShrink: 0 }}>{role.icon}</span>
            <div>
              <div style={{ fontFamily: display, fontWeight: 800, fontSize: 14, color: selected === role.id ? role.color : 'var(--t1)' }}>{role.label}</div>
              <div style={{ fontSize: 11, color: 'var(--t2)', lineHeight: 1.6, marginTop: 2 }}>{role.sub}</div>
            </div>
            {selected === role.id && (
              <span style={{ marginLeft: 'auto', color: role.color, fontSize: 16, flexShrink: 0 }}>✓</span>
            )}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--t3)', fontFamily: mono, fontSize: 10, letterSpacing: '.1em' }}>← BACK</button>
        <button
          onClick={onNext}
          disabled={!selected}
          style={{
            padding: '10px 22px', background: selected ? 'var(--cyan)' : 'var(--b1)', border: 'none', borderRadius: 5,
            color: selected ? '#020508' : 'var(--t3)', fontFamily: mono, fontSize: 10, letterSpacing: '.12em', fontWeight: 900, cursor: selected ? 'pointer' : 'not-allowed',
          }}
        >
          NEXT →
        </button>
      </div>
    </div>
  );
}

// ── Step 2: Recommendation ────────────────────────────────────────────────────
function StepRecommendation({ roleId, onNavigate, onNext, onBack }) {
  const role = ROLES.find((r) => r.id === roleId) || ROLES[1];
  const rec = role.recommendation;
  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div style={{ fontFamily: mono, fontSize: 9, color: 'var(--t3)', letterSpacing: '.14em', marginBottom: 10 }}>STEP 2 OF 3</div>
        <h2 style={{ fontFamily: display, fontWeight: 900, fontSize: 22, color: 'var(--t1)', lineHeight: 1.2 }}>
          Your first workspace
        </h2>
        <p style={{ fontSize: 12, color: 'var(--t2)', marginTop: 8 }}>Based on your role, here's where to start.</p>
      </div>

      <div style={{
        background: `color-mix(in srgb, ${rec.color} 6%, var(--surface))`,
        border: `1px solid color-mix(in srgb, ${rec.color} 25%, transparent)`,
        borderRadius: 10, padding: '22px 20px', marginBottom: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 8,
            background: `color-mix(in srgb, ${rec.color} 14%, transparent)`,
            border: `1px solid color-mix(in srgb, ${rec.color} 30%, transparent)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0,
          }}>
            {rec.icon}
          </div>
          <div>
            <div style={{ fontSize: 9, color: rec.color, letterSpacing: '.14em', fontFamily: mono, fontWeight: 800 }}>RECOMMENDED START</div>
            <div style={{ fontFamily: display, fontWeight: 900, fontSize: 17, color: rec.color }}>{rec.label}</div>
          </div>
        </div>
        <p style={{ fontSize: 12, color: 'var(--t1)', lineHeight: 1.8, margin: 0 }}>{rec.desc}</p>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 28, flexWrap: 'wrap' }}>
        <button
          onClick={() => onNavigate(rec.module)}
          style={{
            flex: 1, padding: '11px 18px', background: rec.color, border: 'none', borderRadius: 6,
            color: '#020508', fontFamily: mono, fontSize: 10, letterSpacing: '.12em', fontWeight: 900, cursor: 'pointer', minWidth: 120,
          }}
        >
          OPEN {rec.label.toUpperCase()} →
        </button>
        <button
          onClick={onNext}
          style={{
            padding: '11px 18px', background: 'transparent', border: '1px solid var(--b1)', borderRadius: 6,
            color: 'var(--t2)', fontFamily: mono, fontSize: 10, letterSpacing: '.1em', cursor: 'pointer',
          }}
        >
          SHOW ME FIRST →
        </button>
      </div>

      <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--t3)', fontFamily: mono, fontSize: 10, letterSpacing: '.1em' }}>← BACK</button>
    </div>
  );
}

// ── Step 3: Free tier + optional upgrade ──────────────────────────────────────
function StepFreeQuotas({ roleId, onUpgrade, onFinish }) {
  const isResearcher = roleId === 'researcher';
  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ fontFamily: mono, fontSize: 9, color: 'var(--t3)', letterSpacing: '.14em', marginBottom: 10 }}>STEP 3 OF 3</div>
        <h2 style={{ fontFamily: display, fontWeight: 900, fontSize: 22, color: 'var(--t1)', lineHeight: 1.2 }}>
          Your free tier
        </h2>
        <p style={{ fontSize: 12, color: 'var(--t2)', marginTop: 8, lineHeight: 1.7 }}>
          Free accounts get monthly limits on the main research workflow hubs. All 50+ interactive labs are unlimited.
        </p>
      </div>

      <div style={{ display: 'grid', gap: 8, marginBottom: 24 }}>
        {FREE_QUOTAS.map((q) => (
          <div key={q.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 11, color: 'var(--t2)', minWidth: 150, flexShrink: 0 }}>{q.label}</div>
            <SmallBar pct={(q.cap / 100) * 100} color="var(--cyan)" />
            <div style={{ fontSize: 10, color: 'var(--t3)', fontFamily: mono, minWidth: 70, textAlign: 'right', flexShrink: 0 }}>{q.cap}/month</div>
          </div>
        ))}
      </div>

      {isResearcher ? (
        <div style={{
          background: 'color-mix(in srgb, var(--cyan) 6%, var(--surface))',
          border: '1px solid color-mix(in srgb, var(--cyan) 25%, transparent)',
          borderRadius: 8, padding: '16px', marginBottom: 20,
        }}>
          <div style={{ fontSize: 9, color: 'var(--cyan)', letterSpacing: '.14em', fontFamily: mono, marginBottom: 8 }}>RESEARCHER PLAN INCLUDES</div>
          <div style={{ fontSize: 11, color: 'var(--t2)', lineHeight: 1.8 }}>
            Unlimited runs on all 7 hubs · AI agents with saved memory · Automation workflows · Priority exports
          </div>
          <button
            onClick={onUpgrade}
            style={{
              marginTop: 12, padding: '9px 18px', background: 'var(--cyan)', border: 'none', borderRadius: 5,
              color: '#020508', fontFamily: mono, fontSize: 10, letterSpacing: '.12em', fontWeight: 900, cursor: 'pointer',
            }}
          >
            VIEW PLANS
          </button>
        </div>
      ) : (
        <div style={{ padding: '10px 14px', background: 'var(--deep)', border: '1px solid var(--b0)', borderRadius: 6, marginBottom: 20, fontSize: 11, color: 'var(--t3)' }}>
          Free accounts reset caps on the 1st of each month. Upgrade anytime to remove limits.
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          onClick={onFinish}
          style={{
            padding: '12px 26px', background: 'var(--cyan)', border: 'none', borderRadius: 6,
            color: '#020508', fontFamily: mono, fontSize: 10, letterSpacing: '.12em', fontWeight: 900, cursor: 'pointer',
          }}
        >
          START RESEARCHING →
        </button>
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export function shouldShowOnboarding() {
  try {
    return !localStorage.getItem('dd_onboarding_done');
  } catch {
    return false;
  }
}

export function markOnboardingDone() {
  try {
    localStorage.setItem('dd_onboarding_done', '1');
  } catch {}
}

export default function OnboardingFlow({ userName, onNavigate, onUpgrade, onClose }) {
  const [step, setStep] = useState(0);
  const [selectedRole, setSelectedRole] = useState(null);

  const handleFinish = (navigateTo = null) => {
    markOnboardingDone();
    if (navigateTo) {
      onNavigate?.(navigateTo);
    }
    onClose?.();
  };

  const handleNavigate = (moduleId) => {
    markOnboardingDone();
    onNavigate?.(moduleId);
    onClose?.();
  };

  const stepContent = (() => {
    switch (step) {
      case 0:
        return <StepWelcome name={userName} onNext={() => setStep(1)} />;
      case 1:
        return (
          <StepRole
            selected={selectedRole}
            onSelect={setSelectedRole}
            onNext={() => setStep(2)}
            onBack={() => setStep(0)}
          />
        );
      case 2:
        return (
          <StepRecommendation
            roleId={selectedRole || 'builder'}
            onNavigate={handleNavigate}
            onNext={() => setStep(3)}
            onBack={() => setStep(1)}
          />
        );
      case 3:
        return (
          <StepFreeQuotas
            roleId={selectedRole || 'builder'}
            onUpgrade={() => { markOnboardingDone(); onUpgrade?.(); onClose?.(); }}
            onFinish={() => handleFinish()}
          />
        );
      default:
        return null;
    }
  })();

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
        background: 'rgba(2,5,8,.88)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <div
        style={{
          width: '100%', maxWidth: 500,
          background: 'var(--surface)',
          border: '1px solid var(--b1)',
          borderRadius: 12,
          overflow: 'hidden',
          boxShadow: '0 28px 80px rgba(0,0,0,.5)',
          animation: 'authSlideUp .28s cubic-bezier(.16,1,.3,1)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '18px 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 26, height: 26, border: '1.5px solid var(--cyan)', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontFamily: display, fontWeight: 800, fontSize: 13, color: 'var(--cyan)' }}>Ð</span>
            </div>
            <div style={{ fontFamily: mono, fontSize: 10, color: 'var(--cyan)', letterSpacing: '.14em' }}>DEGEN DEN SETUP</div>
          </div>
          <button
            onClick={() => handleFinish()}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--t3)', fontSize: 14, padding: 4 }}
            aria-label="Skip onboarding"
          >
            ✕
          </button>
        </div>

        {/* Step indicator */}
        <div style={{ padding: '14px 20px 0' }}>
          <StepDots current={step} />
        </div>

        {/* Step content */}
        <div style={{ padding: '4px 24px 28px' }}>
          {stepContent}
        </div>
      </div>
    </div>
  );
}
